import React, { useEffect } from "react";
import PropTypes from "prop-types";

const HardCodedComponent = ({
	id,
	markup,
	javaScriptFiles,
	cssFiles,
	javaScriptContainer,
	onFileLoad,
	cssContainer,
	cleanupScript,
}) => {
	useEffect(() => {
		const scripts = [];
		const styles = [];
		let inlineScript;

		if (javaScriptContainer) {
			inlineScript = document.createElement("script");
			inlineScript.id = id;
			inlineScript.type = "text/javascript";
			inlineScript.innerHTML = javaScriptContainer;
			scripts.push(inlineScript);
		}

		if (inlineScript && !onFileLoad) {
			document.head.appendChild(inlineScript);
		}

		if (javaScriptFiles?.length) {
			javaScriptFiles.forEach(url => {
				const script = document.createElement("script");
				script.type = "text/javascript";
				script.src = url;

				if (inlineScript && onFileLoad) {
					script.onload = () => {
						// Since the inline script is added after the script is loaded, it may not get picked up by the useEffect clean up
						// This checks to see if a dangling script is still in the DOM and tries to clean up manually first before loading it again
						try {
							if (document.getElementById(id)) {
								document.getElementById(id).parentNode.removeChild(document.getElementById(id));
							}
						} catch (err) {
							console.warn(`Unable to unload script id ${id}`, err);
						}
						document.head.appendChild(inlineScript);
					};
				}

				scripts.push(script);
				document.head.appendChild(script);
			});

			// Load external CSS files
			if (cssFiles) {
				cssFiles?.forEach((url, i) => {
					styles[i] = document.createElement("link");
					styles[i].rel = "stylesheet";
					styles[i].type = "text/css";
					styles[i].href = url;
					document.head.appendChild(styles[i]);
				});
			}
		}

		let style;
		if (cssContainer) {
			style = document.createElement("style");
			document.head.appendChild(style);
			style.appendChild(document.createTextNode(cssContainer));
		}

		return () => {
			if (cleanupScript) {
				// Necessary evil to run user (Contentful) defined clean up script
				// e.g. running window.WEB_CDN_SWC.destroy(); to clean up the instance on unmount
				try {
					// eslint-disable-next-line no-eval
					eval(cleanupScript);
				} catch (err) {
					console.error("Unable to run clean up script:\n", cleanupScript);
				}
			}

			if (scripts?.length) {
				scripts.forEach(script => {
					try {
						script.parentNode.removeChild(script);
					} catch (err) {
						console.warn("Unable to unload script", script);
					}
				});
			}

			if (style) {
				style.parentNode.removeChild(style);
			}
			if (styles?.length) {
				styles.forEach(styleLink => {
					if (styleLink && styleLink.parentNode) {
						styleLink.parentNode.removeChild(styleLink);
					}
				});
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// eslint-disable-next-line react/no-danger
	return markup && <div dangerouslySetInnerHTML={{ __html: markup }} />;
};

HardCodedComponent.propTypes = {
	id: PropTypes.string.isRequired,
	markup: PropTypes.string,
	javaScriptFiles: PropTypes.arrayOf(PropTypes.string),
	cssFiles: PropTypes.arrayOf(PropTypes.string),
	javaScriptContainer: PropTypes.string,
	onFileLoad: PropTypes.bool,
	cssContainer: PropTypes.string,
	cleanupScript: PropTypes.string,
};

HardCodedComponent.defaultProps = {
	markup: undefined,
	javaScriptContainer: undefined,
	javaScriptFiles: undefined,
	onFileLoad: false,
	cssContainer: undefined,
	cleanupScript: undefined,
};

export { HardCodedComponent };
